import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';
import Toggle from '~ComponentsImproved/Toggle';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useLicenses, useLogger } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import useI18n from '~Translations';

const ReceiptsSettings: FC = () => {
  const { i18n } = useI18n();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const logger = useLogger('settings-v2');
  const licenses = useLicenses();

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Receipt)', { fields: data });

    commitToStorage(data);
  }

  const togglesConfig: Array<{
    label: string;
    subLabel?: string;
    key: string;
    storage: any;
    // Will reverse the value of the storage
    inverted?: boolean;
  }> = [
    {
      label: i18n('Settings.ShortenChequeItemNames'),
      subLabel: i18n('Settings.ShortenChequeItemNames.Text'),
      key: 'truncateReceiptItemNames',
      storage: storage.truncateReceiptItemNames,
    },
    {
      label: i18n('Settings.HideZeroAmountItemsInCustomerPrints.Title'),
      key: 'hideZeroAmountItemsInCustomerPrints',
      storage: storage.hideZeroAmountItemsInCustomerPrints,
    },
    {
      label: i18n('Settings.ReceiptShowOnlyOfferName.Title'),
      subLabel: i18n('Settings.ReceiptShowOnlyOfferName.Description'),
      key: 'receiptShowOnlyOfferName',
      storage: storage.receiptShowOnlyOfferName,
    },
  ];

  if (licenses.hasTSR) {
    togglesConfig.push({
      label: i18n('Settings.ShowTipSuggestions.Title'),
      subLabel: i18n('Settings.ShowTipSuggestions.Description'),
      key: 'showTipSuggestions',
      storage: storage.showTipSuggestions,
    });
  }

  const radioButtonsConfig: {
    containerLabel: string;
    containerDesc?: string;
    name: string;
    buttons: Array<{
      id: string;
      label: string;
      value: string | null;
    }>;
  }[] = [
    {
      containerLabel: i18n('Settings.ReceiptSettings'),
      containerDesc: i18n('Settings.ReceiptSettings.Desc'),
      name: 'receiptSettings',
      buttons: [
        {
          id: 'ALWAYS',
          label: i18n('Settings.ReceiptSettings.Always'),
          value: 'ALWAYS',
        },
        {
          id: 'ASK',
          label: i18n('Settings.ReceiptSettings.Ask'),
          value: 'ASK',
        },
        {
          id: 'MANUAL',
          label: i18n('Settings.ReceiptSettings.Manual'),
          value: 'MANUAL',
        },
      ],
    },
  ];

  const handlePrintReceiptSetting = (value: string) => {
    if (value === 'ALWAYS') {
      updateStorage({ askToPrint: false, manualReceiptPrint: false });
    }

    if (value === 'ASK') {
      updateStorage({ askToPrint: true, manualReceiptPrint: false });
    }

    if (value === 'MANUAL') {
      updateStorage({ askToPrint: false, manualReceiptPrint: true });
    }
  };

  const printSettingsSelected = storage.manualReceiptPrint
    ? 'MANUAL'
    : storage.askToPrint
    ? 'ASK'
    : 'ALWAYS';

  if (storageLoading) {
    return null;
  }

  return (
    <Box padding="large">
      <Heading level={4} bottom={8} color="var(--colorWhite)">
        {i18n('Settings.Title')}
      </Heading>
      <SettingsBlock>
        {radioButtonsConfig.map((config) => (
          <RadioButtonSelect
            key={config.name}
            settingKey="receiptSettings" // This is actually also manualReceiptPrint key but in backoffice they're set in paralell so this is fine
            data={config}
            selected={printSettingsSelected}
            handleChange={(value) => handlePrintReceiptSetting(value)}
          />
        ))}
        {togglesConfig.map((toggle) => (
          <Toggle
            key={toggle.key}
            settingKey={toggle.key}
            label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
            description={toggle.subLabel}
            initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
            onToggle={() => updateStorage({ [toggle.key]: !toggle.storage })}
            fullWidth
            darkBackground
            lightText
          />
        ))}
      </SettingsBlock>
    </Box>
  );
};

export default ReceiptsSettings;
